<template>
  <div class="container py-3">
    <!-- Page Header -->
    <div class="page-header">
      <h2>{{ $t('category.title') }}</h2>
      <button class="btn btn-primary" @click="openModal()">{{ $t('category.add') }}</button>
    </div>

    <!-- Search -->
    <div class="search-wrapper mb-3">
      <span class="search-icon">🔍</span>
      <input v-model="search" type="text" class="form-control" :placeholder="$t('category.searchPlaceholder')" />
    </div>

    <!-- Categories Table -->
    <div class="table-card">
      <table class="premium-table table">
        <thead>
          <tr>
            <th>{{ $t('common.name') }}</th>
            <th>{{ $t('common.description') }}</th>
            <th>{{ $t('common.company') }}</th>
            <th>{{ $t('common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="categories.length === 0">
            <td colspan="4"><div class="empty-state"><div class="empty-icon">🏷️</div><p>No categories found</p></div></td>
          </tr>
          <tr v-for="cat in categories" :key="cat.id">
            <td><strong>{{ cat.name }}</strong></td>
            <td>{{ cat.description }}</td>
            <td>{{ cat.company_name }}</td>
            <td>
              <button class="btn-action btn-action-warning" @click="openModal(cat)">✏️ {{ $t('common.edit') }}</button>
              <button class="btn-action btn-action-danger" @click="deleteCategory(cat.id)">🗑️ {{ $t('common.delete') }}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <MainPagination
      v-model:currentPage="currentPage"
      v-model:pageSize="pageSize"
      :total="totalRecords"
    />

    <!-- Modal -->
    <div class="modal fade" id="categoryModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content p-3">
          <div class="modal-header">
            <h5 class="modal-title">{{ isEdit ? $t('category.edit') : $t('category.add') }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>

          <div class="modal-body">
            <form @submit.prevent="saveCategory">
              <div class="mb-3">
                <label class="form-label">{{ $t('common.name') }}</label>
                <input v-model="form.name" type="text" class="form-control" required />
              </div>

              <div class="mb-3">
                <label class="form-label">{{ $t('common.description') }}</label>
                <textarea v-model="form.description" class="form-control"></textarea>
              </div>

              <div class="text-end">
                <button type="button" class="btn btn-secondary me-2" data-bs-dismiss="modal">{{ $t('common.cancel') }}</button>
                <button type="submit" class="btn btn-primary">
                  {{ isEdit ? $t('common.update') : $t('common.save') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import api from '../api/axios'
import * as bootstrap from 'bootstrap'
import { useI18n } from 'vue-i18n'
import MainPagination from '../components/MainPagination.vue'

const { t } = useI18n()


const categories = ref([])
const search = ref('')
const currentPage = ref(1)
const pageSize = ref(10)
const totalRecords = ref(0)

const form = ref({ name: '', description: '' })
const isEdit = ref(false)
let modalInstance = null
let currentId = null

// Fetch all categories
const fetchCategories = async () => {
  try {
    const res = await api.get('categories/', {
      params: {
        page: currentPage.value,
        page_size: pageSize.value,
        search: search.value
      }
    })
    categories.value = res.data.results
    totalRecords.value = res.data.count
  } catch (err) {
    console.error('Error loading categories:', err)
  }
}

let searchTimeout = null
watch(search, () => {
  currentPage.value = 1
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    fetchCategories()
  }, 300)
})

watch([currentPage, pageSize], () => {
  fetchCategories()
})

// Open Add/Edit modal
const openModal = (cat = null) => {
  isEdit.value = !!cat
  currentId = cat ? cat.id : null
  form.value = cat ? { name: cat.name, description: cat.description } : { name: '', description: '' }

  const modalEl = document.getElementById('categoryModal')
  modalInstance = new bootstrap.Modal(modalEl)
  modalInstance.show()
}

// Save or update category
const saveCategory = async () => {
  try {
    if (isEdit.value) {
      await api.put(`categories/${currentId}/`, form.value)
    } else {
      await api.post('categories/', form.value)
    }
    modalInstance.hide()
    fetchCategories()
  } catch (err) {
    console.error('Save failed:', err)
  }
}

// Delete category
const deleteCategory = async (id) => {
  if (!confirm(t('category.confirmDelete'))) return
  try {
    await api.delete(`categories/${id}/`)
    fetchCategories()
  } catch (err) {
    console.error('Delete failed:', err)
  }
}

onMounted(() => {
  fetchCategories()
})
</script>
